import React, { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import moment from "moment"
import { useToast } from "~/utils/Toast"

import { formatCurrency, capitalize } from "~/utils/helpers"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import CatchDropdown from "~/library/CatchDropdown"
import ConfirmationModal from "~/library/ConfirmationModal"
import { ArrowLeft } from "lucide-react"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { apiLineItems } from "~/routes"
import LineItem from "~/types/LineItem"
import ModifierGroup from "~/types/ModifierGroup"
import Modifier from "~/types/Modifier"
import {
  getLineItemsBackPath,
  getLineItemsBackTitle,
  clearLineItemsNavigationState,
} from "~/utils/navigationState"

const LineItemView = () => {
  const [isDeleting, setIsDeleting] = useState(false)
  const navigate = useNavigate()
  const { venueSlug } = getAppConfig()
  const { id } = useParams<{ id: string }>()
  const endpoint = apiLineItems.show.path({ venueSlug, id: id! })
  const { data: lineItem, isLoading } = useFetchResource<{ lineItem: LineItem }, "lineItem">(
    endpoint,
    "lineItem",
    {}
  )
  const toast = useToast()

  const renderDetail = (title: string, content: React.ReactNode, size = 6) => {
    return (
      <div className={`col-${size || 6}`}>
        <p className="mt-2 mb-1 text-muted fw-bold font-12 text-uppercase">{title}</p>
        <div className="d-flex">
          <div className="mt-1 font-14 mb-2">{content}</div>
        </div>
      </div>
    )
  }

  const formatPrice = (amount: number) => {
    return formatCurrency({ cents: amount, currency_iso: "USD" })
  }

  const deleteLineItem = () => {
    setIsDeleting(true)
    const req = apiLineItems.destroy({ data: { venueSlug, id: id! } })
    req
      .then(() => {
        toast("Line item deleted successfully", "success")
        clearLineItemsNavigationState()
        navigate("/line-items")
      })
      .catch((res) => {
        toast(
          `Error deleting line item. ${res.body?.errors?.join(", ") || "Please try again"}`,
          "danger"
        )
        setIsDeleting(false)
      })
  }

  return (
    <LoadingWrapper isLoading={isLoading}>
      <PageTitle
        title={
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-link p-0 me-2"
              onClick={() => navigate(getLineItemsBackPath())}
              aria-label={`Go back to ${getLineItemsBackTitle()}`}
              title={`Go back to ${getLineItemsBackTitle()}`}
            >
              <ArrowLeft />
            </button>
            <span>{lineItem?.name}</span>
          </div>
        }
      >
        <CatchDropdown
          label="Actions"
          items={[
            {
              label: "Edit Line Item",
              to: `/line-items/${id}/edit`,
            },
            {
              label: "Delete Line Item",
              className: "text-danger",
              onClick: () => setIsDeleting(true),
            },
          ]}
        />
      </PageTitle>

      <div className="row">
        <div className="col-8">
          <CatchCard title="Line Item Details">
            <div className="row">
              {renderDetail("Name", lineItem?.name, 6)}
              {renderDetail("Category", capitalize(lineItem?.categoryName) || "—", 6)}
              {renderDetail("Minimum Order Qty", lineItem?.minOrderQty || "—", 6)}
              {renderDetail(
                "Status",
                <span
                  className={`badge badge-lg ${lineItem?.active ? "bg-success" : "bg-secondary"}`}
                >
                  {lineItem?.active ? "Active" : "Inactive"}
                </span>,
                6
              )}
              {lineItem?.description && renderDetail("Description", lineItem.description, 12)}
            </div>
          </CatchCard>

          <CatchCard title="Modifiers">
            {lineItem?.modifierGroups && lineItem.modifierGroups.length > 0 ? (
              <div className="modifier-groups">
                {lineItem.modifierGroups.map((group: ModifierGroup, groupIndex: number) => (
                  <div key={group.id || groupIndex} className="modifier-group mb-3">
                    <div className="bg-primary text-white px-3 py-2 rounded-top">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-bold">{group.name}</span>
                        <div className="d-flex gap-2">
                          <span
                            className={`badge ${
                              group.required ? "bg-warning" : "bg-light"
                            } text-dark`}
                          >
                            {group.required ? "Required" : "Optional"}
                          </span>
                          <span className="badge bg-light text-dark">
                            {group.minAllowed ? `Min: ${group.minAllowed}` : ""}
                          </span>
                          <span className="badge bg-light text-dark">
                            {group.maxAllowed ? `Max: ${group.maxAllowed}` : ""}
                          </span>
                        </div>
                      </div>
                    </div>

                    {group.modifiers && group.modifiers.length > 0 ? (
                      <div className="border border-top-0 rounded-bottom">
                        {group.modifiers.map((modifier: Modifier, modifierIndex: number) => (
                          <div
                            key={modifier.id || modifierIndex}
                            className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom"
                          >
                            <span>{modifier.name}</span>
                            <span className="text-muted">
                              {formatPrice(modifier.priceCents || 0)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="border border-top-0 rounded-bottom px-3 py-2">
                        <p className="text-muted font-12 mb-0">
                          No modifiers defined for this group
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-3 py-2">
                <p className="text-muted mb-0">No modifiers defined for this line item</p>
              </div>
            )}
          </CatchCard>
        </div>

        <div className="col-4">
          <CatchCard title="Pricing Details">
            <div className="row">
              {renderDetail("Unit Amount", formatPrice(lineItem?.amountCents), 12)}
            </div>
          </CatchCard>

          <CatchCard title="Internal Details">
            <div className="row">
              {renderDetail(
                "Created on",
                moment(lineItem?.createdAt).format("MMM Do YYYY hh:mma"),
                12
              )}
              {lineItem?.updatedAt &&
                renderDetail(
                  "Updated on",
                  moment(lineItem?.updatedAt).format("MMM Do YYYY hh:mma"),
                  12
                )}
            </div>
          </CatchCard>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isDeleting}
        onClose={() => setIsDeleting(false)}
        onConfirm={deleteLineItem}
        title="Delete Line Item?"
        description="This action cannot be undone. Are you sure you want to delete this line item?"
      />
    </LoadingWrapper>
  )
}

export default LineItemView
